import React, {useEffect, useMemo, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Button, Divider, Grid, Paper, Typography} from "@mui/material";
import {FormikProps} from "formik";
import {useLocation, useNavigate} from "react-router-dom";
import {formValidation} from "../../../utils/formValidation";
import {AxiosError} from "axios";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {auctionService} from "../../../api/services/auction";
import {State} from "../../../redux/store";
import {
    NewAuctionCustomerForm,
    NewAuctionContestForm,
    NewAuctionForm,
    NewAuctionDatesForm,
    NewAuctionParticipantForm
} from "../../../types/newAuction";
import {dateToString} from "../../../utils/dates";
import {
    AuctionCategory,
    AuctionType,
    BidStep,
    BidDirection,
    AuctionState
} from "../../../utils/enums";
import {
    CustomerFields,
    ContestFields,
    DatesFields,
    ParticipantFields
} from "./components";
import {Company} from "../../../types/company";
import {Auction, AuctionLot, AuctionLotList, HashAuctionItem} from "../../../types/auction";
import {Response} from "../../../types/api";
import {useSnack} from "../../../utils/useSnack";
import {LotFields} from "./components/Lot";
import {LotWithItemList} from "../../../types/lot";
import {hash} from "../../../utils/hash";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {SET_AUCTION} from "../../../redux/reducers/auction";
import digitsOnly from "../../../utils/digitsOnly";

export default function NewAuction({
                                       title, auctionType, auctionPath, auctionEditing, setAuctionEditing
                                   }: {
    title: string;
    auctionType: AuctionType;
    auctionPath: string;
    auctionEditing?: boolean;
    setAuctionEditing?: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const {t} = useTranslation();
    const {snack} = useSnack();
    const navigate = useNavigate();

    const [auctionLots, setAuctionLots] = useState<AuctionLotList[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const onClose = () => {
        if (auctionEditing && setAuctionEditing) {
            setAuctionEditing(false);
        } else {
            navigate(`/account-${auctionPath}`)
        }
    };
    const state = useSelector((state: State) => state);
    const company: Company = state.company.company;

    const customerFormikRef = React.useRef<FormikProps<NewAuctionCustomerForm>>(null);
    const contestFormikRef = React.useRef<FormikProps<NewAuctionContestForm>>(null);
    // const rubricFormikRef = React.useRef<FormikProps<NewAuctionRubricForm>>(null);
    // const termsFormikRef = React.useRef<FormikProps<NewAuctionTermsForm>>(null);
    const datesFormikRef = React.useRef<FormikProps<NewAuctionDatesForm>>(null);
    // const filesFormikRef = React.useRef<FormikProps<NewAuctionFilesForm>>(null);
    const participantFormikRef = React.useRef<FormikProps<NewAuctionParticipantForm>>(null);

    const formikRefs = [
        customerFormikRef,
        contestFormikRef,
        // rubricFormikRef,
        // termsFormikRef,
        datesFormikRef,
        // filesFormikRef,
    ];

    {/* EDITING */
    }
    const location = useLocation();
    const pathArray = location.pathname.split('/');
    const auctionId = auctionEditing ? pathArray[pathArray.length - 1] : "";

    useEffect(() => {
        if (!!auctionId) {
            getAuction()
        }
    }, [auctionId]);
    const dispatch = useDispatch();

    const detailsErrorMessage = (): string => {
        switch (auctionType) {
            case AuctionType.tender:
                return t("snack.error.tender_details")
            case AuctionType.price_offer:
                return t("snack.error.price-offer_details")
            case AuctionType.single_source:
                return t("snack.error.single-source_details")
            default:
                return t("snack.error.auction_details")
        }
    }

    async function getAuction() {
        if (!company) {
            return
        }
        setLoading(true);

        try {
            const response = await auctionService.getAuction(company.id, +auctionId!);
            if (response.status === "success" && response.data) {
                dispatch({type: SET_AUCTION, payload: response.data});
                setAuctionLots(response.data?.lots?.map((lot: AuctionLot) => {
                    return {
                        id: lot.id,
                        lotId: lot.lotDto.id,
                        attachments: lot.lotDto.attachments,
                        lotItems: lot.lotDto.lotItems,
                        auctionDate: lot.lotDto.auctionDate,
                        year: lot.lotDto.year,
                        companyName: lot.lotDto.companyName,
                        itn: lot.lotDto.itn,
                        gross: lot.lotDto.gross,
                        itemsCount: lot.lotDto.itemsCount,
                        createdAt: lot.lotDto.createdAt,
                        state: lot.lotDto.state,
                        auctionType: lot.lotDto.auctionType
                    }
                }))
                return
            } else {
                snack(detailsErrorMessage(), "error")
            }

        } catch (error) {
            const err = error as AxiosError
        } finally {
            setLoading(false)
        }
    }

    const auction: Auction = state.auction.auction;
    const editingInitialState = auctionEditing ? {
        customer: {supervisor: {...auction.supervisor, username: "", role: ""}},
        contest: {
            title: auction.title,
            auctionCategory: auctionType === AuctionType.auction
                ? {
                    value: auction.auctionCategory,
                    label: t(`auction_category.${auction.auctionCategory}`)
                }
                : null,
            auctionType: {
                value: auction.auctionType,
                label: t(`auction_type.${auction.auctionType}`)
            },
            description: auction.description,
            bidStep: auctionType === AuctionType.auction
                ? {
                    value: auction.bidStep,
                    label: t(`bid_step.${auction.bidStep}`)
                }
                : null,
            bidDirection: auctionType === AuctionType.auction
                ? {
                    value: auction.bidDirection,
                    label: t(`bid_direction.${auction.bidDirection}`)
                }
                : null
        },
        participant: {
            participantItn: auction.participant
                ? digitsOnly(auction.participant.company.itn)
                : "",
            participantId: auction.participant
                ? auction.participant.company.id
                : null,
            company: {
                exists: !!auction?.participant,
                title: auction?.participant?.company?.title || null,
                id: auction?.participant?.company?.id || null,
                shortTitle: auction?.participant?.company?.shortTitle || null,
                legalForm: auction?.participant?.company?.legalForm || null,
                itn: digitsOnly(auction?.participant?.company?.itn) || null
            }
        },
        dates: {
            registrationOpenTime: auction.registrationOpenTime ? new Date(auction.registrationOpenTime) : null,
            registrationCloseTime: auction.registrationCloseTime ? new Date(auction.registrationCloseTime) : null,
            bidOpenTime: auction.bidOpenTime ? new Date(auction.bidOpenTime) : null,
            bidCloseTime: auction.bidCloseTime ? new Date(auction.bidCloseTime) : null,
        }
    } : undefined;
    {/* / EDITING */
    }

    const validateAll = () => formikRefs.forEach(ref => formValidation(ref));
    const allIsValid = () => formikRefs
        .map(ref => ref.current?.isValid)
        .every(item => item);

    const validateParticipant = () => auctionType === AuctionType.single_source
        ? formValidation(participantFormikRef)
        : true;
    const participantIsValid = () => auctionType === AuctionType.single_source
        ? participantFormikRef.current?.isValid ? true : false
        : true;

    const submit = async () => {
        if (!company) {
            return
        }

        const allRefsDefined = (
            customerFormikRef.current &&
            contestFormikRef.current &&
            // rubricFormikRef.current &&
            // termsFormikRef.current &&
            datesFormikRef.current &&
            // filesFormikRef.current &&
            true
        );

        validateAll();
        validateParticipant();

        if (allRefsDefined && allIsValid() && participantIsValid()) {
            const customer = customerFormikRef.current.values;
            const contest = contestFormikRef.current.values;
            // const rubric = rubricFormikRef.current.values;
            // const terms = termsFormikRef.current.values;
            const dates = datesFormikRef.current.values;
            // const files = filesFormikRef.current.values;
            const participant = participantFormikRef.current?.values;

            const data: NewAuctionForm = {
                supervisorId: customer.supervisor.userId,
                title: contest.title,
                auctionCategory: auctionType === AuctionType.auction ? contest.auctionCategory?.value as AuctionCategory : null,
                auctionType: contest.auctionType.value as AuctionType,
                description: contest.description,
                bidStep: auctionType === AuctionType.auction ? contest.bidStep?.value as BidStep : null,
                bidDirection: auctionType === AuctionType.auction ? contest.bidDirection?.value as BidDirection : null,
                lotIds: auctionLots.map(auctionLot => auctionLot.lotId),
                // productType: rubric.productType.value as ProductType,
                // items: rubric.items.map(item => ({ ...item,
                // 	unit: item.unit.value as Unit,
                // 	deliveryRegion: item.deliveryRegion.value as DeliveryRegion,
                // 	price: item.price.toString().includes(" ") ? item.price.replace(/\s/g, "") : item.price,
                // 	amount: item.amount.toString().includes(" ") ? item.amount.replace(/\s/g, "") : item.amount
                // })),
                // deliveryCondition: terms.deliveryCondition.value as DeliveryCondition,
                // deliveryTime: terms.deliveryTime,

                registrationOpenTime: dateToString(dates.registrationOpenTime) as string,
                registrationCloseTime: dateToString(dates.registrationCloseTime) as string,
                bidOpenTime: dateToString(dates.bidOpenTime) as string,
                bidCloseTime: dateToString(dates.bidCloseTime) as string,
                // attachments: files.attachments.map(file => file.id),
                participantId: participant ? participant.participantId : null
            };


            try {
                let response: Response<Auction>;
                if (auctionEditing) {
                    response = await auctionService.editAuction(company.id, auctionId, data)
                } else {
                    response = await auctionService.createAuction(company.id, data)
                }

                if (response && response.data && response.status === "success") {
                    snack(
                        auctionEditing
                            ? t("snack.success.auction_edited")
                            : t("snack.success.auction_created"),
                        "success"
                    );
                    navigate(`/account-${auctionPath}/${response.data.id}`);
                    if (setAuctionEditing) setAuctionEditing(false);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            } catch (error) {
                const err = error as AxiosError;
                console.log(err.response);
                snack(t("snack.error.default"), "error");
            }
        } else {
            snack(t("snack.warning.required_fields"), "warning");
        }
    };

    const getItemHashKey = useMemo(() => {
        return (item: AuctionLotList | LotWithItemList) => {
            const hashItem: HashAuctionItem = {
                auctionDate: item.auctionDate,
                year: item.year,
                itemsCount: item.itemsCount,
                itn: item.itn,
                companyName: item.companyName,
                gross: item.gross,
                lotItems: item.lotItems,
            };

            return hash.computeObjectHash(hashItem);
        };
    }, []);

    const addNewLot = async (newLotItemIds: number[]) => {

        return Promise.resolve([] as AuctionLotList[])
    }

    const pickLots = async (lots: LotWithItemList[]) => {
        if(AuctionType.auction === auctionType && lots.length > 1) {
            snack(t("snack.error.auction_lot_count_exceeds_1"), "error")
            return
        }
        let newAuctionLotIds: number[] = [];
        const itemsMap = new Map<string, LotWithItemList>()
        auctionLots.forEach((prevItem: LotWithItemList) => {

            // Добавляем предыдущие значения

            const hashKey = getItemHashKey(prevItem)
            itemsMap.set(hashKey, prevItem)
        });

        // Добавляем новые значения, перезаписывая при совпадении id
        lots.forEach((item: LotWithItemList) => {
            const hashKey = getItemHashKey(item);
            if (itemsMap.has(hashKey)) {
                snack(t("snack.error.lot_duplicate"), "error")
            } else if (!auctionEditing) {
                const newItem: AuctionLotList = (JSON.parse(JSON.stringify(item))) as unknown as AuctionLotList
                //@ts-ignore
                newItem.id = null
                newItem.auctionDate = item.auctionDate
                newItem.year = item.year
                newItem.companyName = item.companyName
                newItem.itn = item.itn
                newItem.gross = item.gross
                newItem.itemsCount = item.itemsCount
                newItem.lotItems = item.lotItems
                newItem.lotId = item.id!
                itemsMap.set(hashKey, newItem)
            } else {
                newAuctionLotIds.push(item.id!)
            }
        });

        if (!auctionEditing) {
            // @ts-ignore
            setAuctionLots(prev => [...prev, ...Array.from(itemsMap.values())])
        } else if (newAuctionLotIds.length > 0) {
            try {
                setLoading(true);
                let newLotItems: AuctionLotList[] = await addNewLot(newAuctionLotIds)
                setAuctionLots(prev => [...prev, ...newLotItems])
            } catch (error) {
                snack(t("snack.error.default"), "error")
            } finally {
                setLoading(false);
            }
        }
    }
    return (
        <Paper sx={{pl: 4, pr: 4, pt: 2, pb: 4}} elevation={10}>
            <Grid container spacing={3} justifyContent="space-between">
                <Grid item>
                    <Typography component="h5" variant="h5"
                                sx={{fontWeight: 700}}>
                        {auctionEditing
                            ? `${title} №${auction.auctionNo}`
                            : title
                        }
                    </Typography>
                </Grid>
            </Grid>

            <Divider sx={{mt: 2, mb: 6, width: "100%"}}/>

            <Grid container rowSpacing={3}>
                <CustomerFields formikRef={customerFormikRef}
                                editingInitialState={editingInitialState?.customer}/>

                {auctionType === AuctionType.single_source
                    ? <>
                        <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>
                        <ParticipantFields formikRef={participantFormikRef}
                                           editingInitialState={editingInitialState?.participant}
                                           participantCompany={editingInitialState?.participant?.company || null}/>
                    </>
                    : null
                }

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>
                <ContestFields formikRef={contestFormikRef} currentAuctionType={auctionType}
                               editingInitialState={editingInitialState?.contest}/>
                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>

                {/*<RubricFields formikRef={rubricFormikRef} currentAuctionType={auctionType}*/}
                {/*	editingInitialState={editingInitialState?.rubric} />*/}
                <Grid container sx={{mt: 1}}>
                    <Accordion sx={{
                        width: '100%',
                        margin: 0,
                        '&:before': {
                            display: 'none',
                        },
                        borderRadius: 0,
                        boxShadow: 'none'
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography variant="h4" component="h1" gutterBottom>
                                {t("lots.list_title")}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <LotFields company={company} getLots={pickLots} auctionType={auctionType} auctionId={auction?.id || null}
                                       auctionState={auction?.state || AuctionState.draft} auctionLots={auctionLots}/>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>
                {/*<TermsFields formikRef={termsFormikRef} */}
                {/*	editingInitialState={editingInitialState?.terms} />*/}
                {/*	<Divider sx={{ mt: 3, mb: 2, ml: "auto", width: '98%' }} />*/}
                <DatesFields formikRef={datesFormikRef} currentAuctionType={auctionType}
                             editingInitialState={editingInitialState?.dates}/>
                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>
                {/*<FilesFields formikRef={filesFormikRef} */}
                {/*	editingInitialState={editingInitialState?.files} />*/}
            </Grid>

            <Divider sx={{mt: 6, mb: 2, width: "100%"}}/>

            <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                <Grid item xs="auto">
                    <Button fullWidth variant="contained" size="large"
                            onClick={onClose} color="secondary"
                    >
                        {t("auction.cancel")}
                    </Button>
                </Grid>
                <Grid item xs="auto">
                    <Button fullWidth variant="contained" color="primary"
                            onClick={submit} size="large">
                        {auctionEditing
                            ? t("auction.save_changes")
                            : t("auction.create_contest")
                        }
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    )
}
